<template>
  <!-- Autocomplete employé, calqué sur ProductAutocomplete. La table dépasse 46 000 fiches :
       on n'en charge jamais la totalité, la recherche part au serveur à la frappe et les
       résultats rendus sont plafonnés (cf. LIMIT). -->
  <v-select
    :value="selectedOption"
    :options="results"
    :filterable="false"
    label="label"
    :placeholder="placeholder"
    :disabled="disabled"
    class="employee-ac"
    @input="onInput"
    @search="onSearch"
  >
    <!-- Format « PRÉNOM NOM (MATRICULE) », identique à la colonne Responsable de la liste. -->
    <template slot="option" slot-scope="opt">
      <div class="employee-ac__opt">
        {{ opt.label }}
        <span v-if="opt.agence" class="employee-ac__agency">— {{ opt.agence }}</span>
      </div>
    </template>

    <template slot="selected-option" slot-scope="opt">
      <span class="employee-ac__label">{{ opt.label }}</span>
    </template>

    <template slot="no-options" slot-scope="{ search }">
      <div class="employee-ac__empty">
        <template v-if="normalize(search).trim().length < minChars">
          <i class="far fa-keyboard"></i> Tapez au moins {{ minChars }} caractères…
        </template>
        <template v-else>Aucun employé pour «&nbsp;{{ search }}&nbsp;»</template>
      </div>
    </template>

    <li v-if="hiddenCount > 0" slot="list-footer" class="employee-ac__more">
      <i class="far fa-ellipsis-h"></i> {{ hiddenCount }} autre(s) résultat(s) — précisez la recherche
    </li>
  </v-select>
</template>

<script>
// Nombre max de résultats rendus dans le menu (on « fait sauter » le reste).
const LIMIT = 30;
// Longueur minimale de recherche avant d'interroger le serveur.
const MIN_CHARS = 2;
// Délai avant d'interroger le serveur.
const DEBOUNCE = 300;

// Minuscule + suppression des accents, pour un matching tolérant.
function normalize(s) {
  return (s == null ? "" : String(s))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // marques diacritiques combinantes
}

export default {
  name: "EmployeeAutocomplete",
  props: {
    value: { default: null },
    placeholder: { type: String, default: "Rechercher un responsable…" },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return { results: [], hiddenCount: 0, minChars: MIN_CHARS, timer: null };
  },
  computed: {
    employees() {
      return this.$store.getters["employee/getAll"] || [];
    },
    /**
     * Index normalisé, recalculé seulement si la liste du store change.
     *
     * Dédoublonné par GID : plus de 9 000 GID portent deux fiches employé (l'import MCS en a
     * créé avec `number = gaia_id` à côté de celles du flux personnel). On applique ici la même
     * règle que BusinessManagerResolver côté serveur — fiche du flux personnel d'abord, puis la
     * plus ancienne — sans quoi le filtre proposerait deux entrées d'apparence identique dont
     * une seule ramènerait des affaires.
     */
    index() {
      const parGid = new Map();
      const sansGid = [];

      for (const e of this.employees) {
        const gid = (e.gaia_id || "").toUpperCase();
        if (!gid) {
          sansGid.push(e);
          continue;
        }
        const actuel = parGid.get(gid);
        if (!actuel || this.prefere(e, actuel)) parGid.set(gid, e);
      }

      return [...parGid.values(), ...sansGid].map((e) => {
        const identite = [e.firstname, e.lastname].filter(Boolean).join(" ").trim();
        // Les fiches créées depuis le seul libellé du fichier OTP n'ont pas de prénom :
        // on retombe sur le nom seul, puis sur le matricule.
        const matricule = e.gaia_id || e.number || "";
        const nom = identite || matricule;
        return {
          value: e.id,
          label: matricule && identite ? identite + " (" + matricule + ")" : nom,
          agence: e.agency && e.agency.internal_number ? e.agency.internal_number : null,
          hay: normalize(identite + " " + (e.gaia_id || "") + " " + (e.number || "")),
          codeHay: normalize(matricule),
        };
      });
    },
    // L'option sélectionnée est cherchée dans l'index complet (pas dans `results`), pour que
    // le libellé s'affiche même hors résultats filtrés. Comparaison souple : une valeur
    // restaurée depuis l'URL arrive sous forme de chaîne.
    selectedOption() {
      if (this.value == null || this.value === "") return null;
      return this.index.find((o) => String(o.value) === String(this.value)) || null;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler: "ensureSelectionLoaded",
    },
  },
  methods: {
    normalize,
    /** Vrai si `candidat` doit l'emporter sur `actuel` pour un même GID. */
    prefere(candidat, actuel) {
      const duPersonnel = (e) => (e.number || "") !== (e.gaia_id || "");
      if (duPersonnel(candidat) !== duPersonnel(actuel)) return duPersonnel(candidat);
      return candidat.id < actuel.id;
    },
    /**
     * Le filtre peut être restauré depuis l'URL avant que l'employé soit dans le store :
     * sans ce chargement ciblé, le champ afficherait un identifiant nu.
     */
    ensureSelectionLoaded(value) {
      if (value == null || value === "" || this.selectedOption) return;
      this.$store.dispatch("employee/getAllForSelectByIds", { ids: [value], add: true });
    },
    onInput(option) {
      this.$emit("input", option ? option.value : null);
    },
    onSearch(search, loading) {
      const q = normalize(search).trim();
      if (q.length < MIN_CHARS) {
        this.results = [];
        this.hiddenCount = 0;
        return;
      }

      // Le serveur alimente le store ; le classement reste local.
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (loading) loading(true);
        this.$store
          .dispatch("employee/getAllForSelectBySearch", { wait: true, search: search, add: true })
          .then(() => this.computeResults(q))
          .catch(() => this.computeResults(q))
          .then(() => {
            if (loading) loading(false);
          });
      }, DEBOUNCE);
    },
    computeResults(q) {
      const tokens = q.split(/\s+/);
      const scored = [];
      for (const item of this.index) {
        const s = this.scoreItem(item, tokens);
        if (s > 0) scored.push({ item, s });
      }
      scored.sort((a, b) => b.s - a.s || a.item.label.localeCompare(b.item.label));
      this.hiddenCount = Math.max(0, scored.length - LIMIT);
      this.results = scored.slice(0, LIMIT).map((x) => x.item);
    },
    // Tous les mots de la requête doivent matcher ; le score total classe.
    scoreItem(item, tokens) {
      let total = 0;
      for (const t of tokens) {
        const s = this.scoreToken(item, t);
        if (s <= 0) return 0;
        total += s;
      }
      return total;
    },
    // Priorité : matricule exact/préfixe > sous-chaîne (plus tôt = mieux) > sous-séquence.
    scoreToken(item, t) {
      if (item.codeHay === t) return 200;
      if (item.codeHay.indexOf(t) === 0) return 120;
      const idx = item.hay.indexOf(t);
      if (idx === 0) return 100;
      if (idx > 0) return 60 - Math.min(idx, 30);
      return this.isSubsequence(item.hay, t) ? 15 : 0;
    },
    isSubsequence(hay, t) {
      let h = 0;
      for (let i = 0; i < t.length; i++) {
        h = hay.indexOf(t[i], h);
        if (h === -1) return false;
        h++;
      }
      return true;
    },
  },
};
</script>

<style scoped>
/* Racine bornée à la largeur de la colonne, sinon le toggle grandit avec le
   contenu et la valeur déborde du bloc (l'ellipsis ne peut pas se déclencher). */
.employee-ac {
  width: 100%;
}
.employee-ac >>> .vs__dropdown-toggle {
  background: #fff;
  border-color: #e5e6e7;
  border-radius: 3px;
  min-height: 31px;
  max-width: 100%;
  padding: 0; /* défaut asymétrique 0 0 4px → casse le centrage */
  align-items: stretch;
}
.employee-ac >>> .vs__selected-options {
  align-items: center;
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
  padding: 0 2px;
}
.employee-ac >>> .vs__selected {
  font-size: 13px;
  margin: 0 2px;
  max-width: 100%;
  flex-wrap: nowrap;
  overflow: hidden;
}
/* Valeur sélectionnée : forcée sur une seule ligne, tronquée par « … ». */
.employee-ac >>> .vs__selected .employee-ac__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.employee-ac >>> .vs__search {
  font-size: 13px;
  margin: 0;
}
.employee-ac >>> .vs__actions {
  padding-top: 0;
}
.employee-ac >>> .vs__dropdown-menu {
  padding: 4px 0;
}
.employee-ac >>> .vs__dropdown-option {
  padding: 6px 12px;
}
.employee-ac >>> .vs__dropdown-option--highlight {
  background: #1ab394;
  color: #fff;
}
.employee-ac__opt {
  white-space: normal;
  color: #34495e;
  font-size: 13px;
}
.employee-ac >>> .vs__dropdown-option--highlight .employee-ac__opt {
  color: #fff;
}
.employee-ac__label {
  color: #34495e;
  font-size: 13px;
}
/* Agence : distingue deux homonymes dans la liste déroulante. */
.employee-ac__agency {
  color: #909499;
  font-size: 12px;
}
.employee-ac >>> .vs__dropdown-option--highlight .employee-ac__agency {
  color: #e8f7f3;
}
.employee-ac__empty {
  padding: 6px 4px;
  color: #909499;
  font-size: 13px;
}
.employee-ac__more {
  list-style: none;
  padding: 6px 12px;
  font-size: 12px;
  color: #909499;
  border-top: 1px solid #f1f3f5;
  background: #fafbfc;
}
.employee-ac__more i {
  margin-right: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/Global/EmployeeAutocomplete.vue